import * as React from 'react';
import { observer } from 'mobx-react';
import { configConsumer } from '@estee/elc-service';
import { BreakpointsWrapper } from 'src/internal/views/breakpoints-wrapper/BreakpointsWrapper';
import styled from 'styled-components';
import { Container } from '@estee/elc-grid';
import { ContentSpacing } from '@estee/elc-base-theme';
import { PRODUCT_GRID_WRAPPER } from '~constants/DataTestIdAttributes';
import { ScrollIntoView } from 'src/internal/views/scroll-into-view/ScrollIntoView';
import ProductGrid, { Formatter } from 'src/internal/views/product-grid/ProductGrid';
import classnames from 'classnames';
import {
    GridActionsBarTypes,
    IGridActionsBarConfig
} from 'src/internal/views/product-grid/GridHeaderBarWrapper';
import { IFiltersConfig } from '~interfaces/IFilters';
import { IBreakpoints } from '~interfaces/IBreakpoints';
import { IProductBriefCollection } from 'src/internal/views/product-brief-collection/ProductBriefCollection';
import { FilterSet } from '~domain/entities/FilterSet';
import { Filter } from '~domain/entities/Filter';
import { GridItem } from '~domain/entities/GridItem';

export interface IProductGridContent {
    config?: IProductGridContentConfig;
    setProductSortingDropdownValue(_: string, value: string): Promise<void>;
    isAllGiftsFilterApplied: boolean;
    appliedFilters: FilterSet[];
    productsNumber: number;
    isFilterDisplayStacked: boolean;
    areFiltersApplied: boolean;
    productSortingDropdownValue: string;
    filters: FilterSet[];
    scrollTopOffset: number;
    totalAppliedFilters: number;
    extractedAppliedFilters: Filter[];
    positionGridInsideAccordion: boolean;
    isLoaded: boolean;
    gridItems: GridItem[];
    featuredProductId?: string;
}

interface IProductGridContentConfig {
    hideSorting: boolean;
    filters: IFiltersConfig;
    productGridBreakpoints: IBreakpoints;
    gridActionsBar?: IGridActionsBarConfig;
}

const StyledContainer = styled(Container)`
    margin-top: ${ContentSpacing.space20};
`;

const buildBriefProps = (props: IProductGridContent) => {
    const { gridItems, featuredProductId, isLoaded, positionGridInsideAccordion } = props;
    const briefProps: IProductBriefCollection = {
        gridItems,
        isLoaded,
        featuredProductId,
        positionGridInsideAccordion
    };

    return briefProps;
};

export const ProductGridContent = configConsumer([
    'hideSorting',
    'filters',
    'productGridBreakpoints',
    'gridActionsBar'
])(
    observer(function ProductGridContent(props: IProductGridContent) {
        const config = props.config as IProductGridContentConfig;
        const {
            setProductSortingDropdownValue,
            isAllGiftsFilterApplied,
            appliedFilters,
            productsNumber,
            isFilterDisplayStacked,
            areFiltersApplied,
            productSortingDropdownValue,
            filters: filtersProp,
            scrollTopOffset,
            totalAppliedFilters,
            extractedAppliedFilters
        } = props;

        const gridWrapperClassNames = classnames('elc-product-grid-wrapper', {
            'has-filters': filtersProp?.length > 0,
            'filters-applied': areFiltersApplied
        });
        const {
            hideSorting,
            filters: filtersConfig,
            productGridBreakpoints,
            gridActionsBar
        } = config;
        const isStickyActionBar = gridActionsBar?.type === GridActionsBarTypes.STICKY;

        return (
            <BreakpointsWrapper breakpoints={productGridBreakpoints}>
                <StyledContainer
                    className={gridWrapperClassNames}
                    data-test-id={PRODUCT_GRID_WRAPPER}
                >
                    <ScrollIntoView offset={-scrollTopOffset} enabled={isStickyActionBar}>
                        <ProductGrid
                            formatter={Formatter.DEFAULT}
                            briefProps={buildBriefProps(props)}
                            isAllGiftsFilterApplied={isAllGiftsFilterApplied}
                            areFiltersApplied={areFiltersApplied}
                            hideSorting={hideSorting}
                            productsNumber={productsNumber}
                            isFilterDisplayStacked={isFilterDisplayStacked}
                            isFilterDisplayTabbed={!isFilterDisplayStacked}
                            productSortingDropdownValue={productSortingDropdownValue}
                            setProductSortingDropdownValue={setProductSortingDropdownValue}
                            filterSets={filtersProp}
                            appliedFilters={appliedFilters}
                            totalAppliedFilters={totalAppliedFilters}
                            appliedFiltersList={extractedAppliedFilters}
                            filters={filtersConfig}
                        />
                    </ScrollIntoView>
                </StyledContainer>
            </BreakpointsWrapper>
        );
    })
);
